// Bilingual translations: English (en) + Greek (el)
// Keys use namespace.key format. Use {var} placeholders for interpolation.

export const LANGUAGES = [
    { code: "en", label: "English", short: "EN" },
    { code: "el", label: "Ελληνικά", short: "GR" },
];

const en = {
    // Common
    "common.signIn": "Sign in",
    "common.signingIn": "Signing in…",
    "common.signOut": "Logout",
    "common.createAccount": "Create account",
    "common.creatingAccount": "Creating account…",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.trash": "Delete",
    "common.deleting": "Deleting…",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.search": "Search",
    "common.loading": "Loading…",
    "common.email": "Email",
    "common.password": "Password",
    "common.username": "Name",
    "common.surname": "Surname",
    "common.mobile": "Mobile",
    "common.mobileNumber": "Mobile Number",
    "common.name": "Name",
    "common.notes": "Notes",
    "common.yes": "Yes",
    "common.no": "No",
    "common.status": "Status",
    "common.date": "Date",
    "common.comment": "Comment",
    "common.comments": "Comments",
    "common.yourAvailability": "YourAvailability",
    "common.message": "Message",
    "common.passwordsDoNotMatch": "Passwords do not match",
    "common.requiredFields": "Please fill all required fields.",

    // Brand strapline
    "brand.admin": "Admin",
    "brand.controlRoom": "Control room",

    // Landing
    "landing.heroLine1": "Pairing referees with tournaments.",
    "landing.heroLine2": "Fast. Easy. Reliable.",
    "landing.heroSubtitle": "Register. Get verified. Access all tournaments.",
    "landing.iAlreadyHaveAccount": "I already have an account",
    "landing.tagline1": "Would you like to train and work with us?",
    "landing.tagline2": "Are you the owner of a game facility?",
    "landing.tagline3": "Are you interested in partnering with us?",
    "landing.contactLineQuestion": "Are you a referee or a tournament organizer?",
    "landing.contactLineAction": "Drop us a line",
    "landing.feature1Title": "Event Calendar",
    "landing.feature1Sub": "Pick dates & times",
    "landing.feature2Title": "Admin Insight",
    "landing.feature2Sub": "Contact referees fast",
    "landing.feature3Title": "Tournament Feed",
    "landing.feature3Sub": "Live updates for all",
    "landing.contactDialogTitle": "Get in touch",
    "landing.contactMessagePlaceholder": "Tell us a bit about you and what you're looking for.",
    "landing.sendMessage": "Send message",
    "landing.sending": "Sending…",
    "landing.contactSuccess": "Message sent. We'll get back to you soon.",

    // Login
    "login.title": "Log in to your account",
    "login.subtitle": "Enter your credentials below.",
    "login.forgot": "Forgot password?",
    "login.noAccount": "Don't have an account?",
    "login.createOne": "Create one",
    "login.welcomeBack": "Welcome back, {name}",

    // Register
    "register.title": "Create your account",
    "register.subtitle": "Create your account to manage your availability and set your rate",
    "register.confirmPassword": "Re-confirm Password",
    "register.alreadyAccount": "Already have an account?",
    "register.submitSuccess": "Account submitted. Awaiting admin approval.",
    "register.awaitingApproval": "Awaiting approval",
    "register.submittedTitle": "Registration submitted",
    "register.submittedBody": "Hi {name}, an administrator will review your account ({email}) and activate it shortly. You'll be able to sign in once your status is set to Verified.",
    "register.goToLogin": "Go to login",

    // Forgot password
    "forgot.backToLogin": "Back to login",
    "forgot.title": "Forgot your password?",
    "forgot.subtitle": "Enter your email to receive a reset link.",
    "forgot.send": "Send reset link",
    "forgot.sending": "Sending…",
    "forgot.success": "If that account exists, you'll receive a reset link.",
    "forgot.devLink": "Dev reset link",

    // Reset password
    "reset.label": "Reset password",
    "reset.title": "Set a new password",
    "reset.subtitle": "Must be at least 6 characters.",
    "reset.newPassword": "New password",
    "reset.confirm": "Confirm password",
    "reset.submit": "Reset password",
    "reset.submitting": "Resetting…",
    "reset.success": "Password reset. Please log in.",
    "reset.missingToken": "Missing reset token",
    "reset.backToLogin": "Back to login",

    // User Dashboard
    "dashboard.heading": "Dashboard",
    "dashboard.hello": "Hello, {name}.",
    "dashboard.markAvailability": "Blue dates on the calendar are scheduled events. Click to see details and mark your availability and price.",
    "dashboard.eventCalendar": "Event Calendar",
    "dashboard.legendYourSlots": "Your slots",
    "dashboard.legendTournaments": "Tournaments",
    "dashboard.selectedDate": "Selected date",
    "dashboard.workOptionLabel": "Options for work",
    "dashboard.selectedTournamentLabel": "Selected Tournament",
    "dashboard.selectedTournamentPlaceholder": "Tournament name",
    "dashboard.selectedTournamentRequired": "Please select a tournament",
    "dashboard.workMorning": "Morning",
    "dashboard.workAfternoon": "Afternoon",
    "dashboard.workAllDay": "All day",
    "dashboard.commentPlaceholder": "e.g. From 10:00 AM to 1:00 PM",
    "dashboard.commentLabel": "Enter more details about your availability",
    "dashboard.suggestedPriceLabel": "Suggested Compensation",
    "dashboard.suggestedPricePlaceholder": "e.g. €/Hour, €/Day",
    "dashboard.addAvailability": "Add availability",
    "dashboard.saving": "Saving…",
    "dashboard.availabilityAdded": "Availability added",
    "dashboard.removed": "Removed",
    "dashboard.pickDateFirst": "Pick a date first",
    "dashboard.yourAvailability": "Availability & Price",
    "dashboard.slotsCount": "{n} slots",
    "dashboard.noSlots": "You haven't marked any slots yet.",
    "dashboard.upcoming": "Upcoming",
    "dashboard.tournaments": "Tournaments",
    "dashboard.activeCount": "{n} active",
    "dashboard.noTournaments": "No tournaments yet. Check back soon.",
    "dashboard.tournamentsOn": "{date} · Tournaments",
    "dashboard.ageLabel": "Category",
    "dashboard.ageGroup": "Age group",
    "dashboard.format": "Format",
    "dashboard.viewOnMap": "View on map",
    "dashboard.websiteUrl": "Website URL",
    "dashboard.sportType": "Sport Type",

    // Profile
    "profile.back": "Back to dashboard",
    "profile.kicker": "Profile",
    "profile.title": "Your account",
    "profile.photoSection": "Profile photo",
    "profile.uploadPhoto": "Upload photo",
    "profile.changePhoto": "Change photo",
    "profile.uploading": "Uploading…",
    "profile.photoUpdated": "Profile photo updated",
    "profile.personalSection": "Personal details",
    "profile.personalSub": "Year of birth (only the year) and where you currently live.",
    "profile.birthYear": "Year of Birth",
    "profile.birthYearPlaceholder": "e.g. 1995",
    "profile.location": "Place of living",
    "profile.locationPlaceholder": "e.g. Engomi, Nicosia",
    "profile.saveDetails": "Save details",
    "profile.detailsSaved": "Personal details saved",
    "profile.yearRangeError": "Year of birth must be between 1900 and {max}",
    "profile.sportsSection": "Sports & experience",
    "profile.sportsSub": "Add every sport you referee, with years of experience.",
    "profile.experienceLabel": "Referee Experience",
    "profile.experienceYes": "Yes",
    "profile.experienceNo": "No",
    "profile.interestedSportsLabel": "Sports you'd like to learn",
    "profile.interestedSportsPlaceholder": "What sports are you interested in learning to referee?",
    "profile.addSport": "Add sport",
    "profile.noSports": "No sports added yet. Click \"Add sport\" to begin.",
    "profile.sport": "Sport",
    "profile.years": "Years",
    "profile.yearsPlaceholder": "e.g. 1 year",
    "profile.saveSports": "Save sports",
    "profile.sportsSaved": "Sports & experience saved",
    "profile.selectSportError": "Please select a sport for every entry",
    "profile.selectSportPlaceholder": "Select the sports from dropdown",
    "profile.duplicateSport": "Duplicate sport: {name}",
    "profile.paymentsSection": "Payment Methods",
    "profile.paymentsSub": "Add the payment methods you accept and any details for each.",
    "profile.addPaymentMethod": "Add Method",
    "profile.paymentMethod": "Payment Method",
    "profile.paymentDetails": "Details",
    "profile.paymentDetailsPlaceholder": "e.g. account number, phone, IBAN…",
    "profile.selectMethodPlaceholder": "Select a payment method",
    "profile.noPayments": "No payment methods added yet. Click \"Add Method\" to begin.",
    "profile.savePayments": "Save payment methods",
    "profile.paymentsSaved": "Payment methods saved",
    "profile.selectMethodError": "Please select a payment method for every row",
    "profile.duplicateMethod": "Duplicate payment method: {name}",

    // Admin
    "admin.kicker": "Admin / overview",
    "admin.title": "Control Room",
    "admin.statReferees": "Referees",
    "admin.statSlots": "Slots",
    "admin.statTournaments": "Tournaments",
    "admin.tabUsers": "Users & Availability",
    "admin.tabTournaments": "Tournaments",
    "admin.tabRestore": "RecycleBin",
    "admin.restoreHeading": "Deleted tournaments",
    "admin.restoreEmpty": "No deleted tournaments. When you delete one from the Tournaments tab, it will appear here.",
    "admin.deletedOn": "Deleted on",
    "admin.restoreTournament": "Restore Tournament",
    "admin.tournamentRestored": "Tournament restored",
    "admin.permanentlyDelete": "Permanently Delete",
    "admin.tournamentPermDeleted": "Tournament permanently deleted",
    "admin.permDeleteConfirmTitle": "Permanently delete this tournament?",
    "admin.permDeleteConfirmBody": "Are you sure you want to delete this Tournament detail Permanently?",
    "admin.permDeleteConfirmAction": "Yes",
    "admin.permDeleteConfirmCancel": "No",
    "admin.restoreConfirmTitle": "Restore this tournament?",
    "admin.restoreConfirmBody": "Are you sure you want to Restore this Tournament details?",
    "admin.restoreConfirmAction": "Yes",
    "admin.restoreConfirmCancel": "No",
    "admin.softDeleteConfirmTitle": "Move to RecycleBin?",
    "admin.softDeleteConfirmBody": "Are you sure you want to move to RecycleBin?",
    "admin.softDeleteConfirmYes": "Yes",
    "admin.softDeleteConfirmNo": "No",
    "admin.editProfileTitle": "Edit profile — {name}",
    "admin.updateProfile": "Update",
    "admin.editConfirmTitle": "Update profile details?",
    "admin.editConfirmBody": "Do you want to update the profile details?",
    "admin.profileUpdated": "Profile updated.",
    "admin.noEntries": "No entries yet. Click Add to insert one.",
    "admin.sportPlaceholder": "Select a sport",
    "admin.yearsPlaceholder": "Years",
    "admin.paymentMethodPlaceholder": "Select a method",
    "admin.paymentDetailsPlaceholder": "Account / details",
    "admin.allTournaments": "All tournaments",
    "admin.addTournament": "Add tournament",
    "admin.noTournamentsAdmin": "No tournaments yet. Add one to publish to all players.",
    "admin.registeredReferees": "Registered Referees",
    "admin.searchPlaceholder": "Search by name, surname, status, email, year of birth, mobile, place, sport, payment…",
    "admin.deleteSelected": "Delete selected ({n})",
    "admin.colUsername": "Name",
    "admin.colStatus": "Status",
    "admin.colNotes": "Notes",
    "admin.colEmail": "Email",
    "admin.colBirthYear": "Year of Birth",
    "admin.colMobile": "Mobile",
    "admin.colLocation": "Place of living",
    "admin.colSportExperience": "Sport & Experience",
    "admin.colPaymentMethod": "Payment Method",
    "admin.colSlots": "Slots",
    "admin.noUsers": "No users yet.",
    "admin.noUsersMatch": "No users match the search.",
    "admin.verified": "Verified",
    "admin.pending": "Pending",
    "admin.activate": "Activate",
    "admin.deactivate": "Deactivate",
    "admin.activated": "Account activated (Verified)",
    "admin.deactivated": "Account deactivated (Pending)",
    "admin.refereesAvailability": "Referees Availability",
    "admin.filterFrom": "From",
    "admin.filterTo": "To",
    "admin.exportLabel": "Export ({n})",
    "admin.colDate": "Date",
    "admin.colOption": "Option",
    "admin.noAvailability": "No availability matches the filter.",
    "admin.exportSuccess": "Exported {n} records as {kind}",
    "admin.exportEmpty": "No records to export",
    "admin.exportFailed": "Export failed",
    "admin.notesForUser": "Notes for {name}",
    "admin.userNotesPlaceholder": "Write notes about this referee — anything that helps you remember (skills, languages, contact preferences, etc.)",
    "admin.saveNotes": "Save notes",
    "admin.notesSaved": "Notes saved",
    "admin.availNotesPlaceholder": "Write notes about this specific availability slot (assigned tournament, follow-up, etc.)",
    "admin.confirmDeleteTitle": "Whether you want to delete this User details Permanently from the system.",
    "admin.confirmDeleteBody": "This will remove {n} referee(s) along with their availability records, verification tokens, password reset tokens, and avatar references. This action cannot be undone.",
    "admin.confirmDeleteAction": "Yes, delete permanently",
    "admin.confirmDeleteAvailTitle": "Delete {n} availability entry/entries?",
    "admin.confirmDeleteAvailBody": "This will permanently remove {n} availability record(s). This action cannot be undone.",
    "admin.availabilityDeleted": "{n} availability record(s) deleted.",
    "admin.availSearchPlaceholder": "Search by option, name, surname, email, mobile, availability, compensation…",
    "admin.deletedCount": "Deleted {n} user(s) permanently",
    "admin.deleteFailedCount": "{n} deletion(s) failed",
    "admin.tournamentRemoved": "Tournament removed",
    "admin.tournamentCreated": "Tournament created",
    "admin.tournamentUpdated": "Tournament updated",
    "admin.pickStartEnd": "Please pick both start and end dates",
    "admin.endBeforeStart": "End date must be on or after start date",

    // Tournament form
    "tdialog.title": "Add Tournament",
    "tdialog.editTitle": "Edit Tournament",
    "tdialog.cloneTitle": "Clone Tournament",
    "common.clone": "Clone",
    "tdialog.eventName": "Tournament Name",
    "tdialog.tournamentUrl": "Tournament URL",
    "common.eventWebsite": "Event Website",
    "tdialog.sportType": "Sport Type",
    "tdialog.eventLocation": "Map Location",
    "tdialog.ageCategories": "Age Categories",
    "tdialog.formatCategories": "Format Categories",
    "tdialog.otherDetails": "Other details",
    "tdialog.otherDetailsPlaceholder": "Any additional information about the event…",
    "tdialog.startDate": "Start date",
    "tdialog.endDate": "End date",
    "tdialog.pickStart": "Pick start date",
    "tdialog.pickEnd": "Pick end date",
    "tdialog.logo": "Logo (optional)",

    // Language switcher
    "lang.label": "Language",
};

const el = {
    // Common
    "common.signIn": "Σύνδεση",
    "common.signingIn": "Σύνδεση…",
    "common.signOut": "Αποσύνδεση",
    "common.createAccount": "Δημιουργία λογαριασμού",
    "common.creatingAccount": "Δημιουργία λογαριασμού…",
    "common.save": "Αποθήκευση",
    "common.saving": "Αποθήκευση…",
    "common.cancel": "Άκυρο",
    "common.delete": "Διαγραφή",
    "common.trash": "Διαγραφή",
    "common.deleting": "Διαγραφή…",
    "common.edit": "Επεξεργασία",
    "common.add": "Προσθήκη",
    "common.search": "Αναζήτηση",
    "common.loading": "Φόρτωση…",
    "common.email": "Email",
    "common.password": "Κωδικός",
    "common.username": "Όνομα",
    "common.surname": "Επώνυμο",
    "common.mobile": "Κινητό",
    "common.mobileNumber": "Αριθμός κινητού",
    "common.name": "Όνομα",
    "common.notes": "Σημειώσεις",
    "common.yes": "Ναι",
    "common.no": "Όχι",
    "common.status": "Κατάσταση",
    "common.date": "Ημερομηνία",
    "common.comment": "Σχόλιο",
    "common.comments": "Σχόλια",
    "common.yourAvailability": "ΗΔιαθεσιμότητάΣας",
    "common.message": "Μήνυμα",
    "common.passwordsDoNotMatch": "Οι κωδικοί δεν ταιριάζουν",
    "common.requiredFields": "Συμπληρώστε όλα τα υποχρεωτικά πεδία.",

    // Brand strapline
    "brand.admin": "Διαχείριση",
    "brand.controlRoom": "Κέντρο ελέγχου",

    // Landing
    "landing.heroLine1": "Συνδέουμε διαιτητές με τουρνουά.",
    "landing.heroLine2": "Γρήγορα. Εύκολα. Αξιόπιστα.",
    "landing.heroSubtitle": "Εγγραφή. Επιβεβαίωση. Πρόσβαση σε όλα τα τουρνουά.",
    "landing.iAlreadyHaveAccount": "Έχω ήδη λογαριασμό",
    "landing.tagline1": "Θέλετε να εκπαιδευτείτε και να εργαστείτε μαζί μας;",
    "landing.tagline2": "Είστε ιδιοκτήτης αθλητικής εγκατάστασης;",
    "landing.tagline3": "Σας ενδιαφέρει να συνεργαστείτε μαζί μας;",
    "landing.contactLineQuestion": "Είστε διαιτητής ή διοργανωτής τουρνουά;",
    "landing.contactLineAction": "Επικοινωνήστε μαζί μας",
    "landing.feature1Title": "Ημερολόγιο εκδηλώσεων",
    "landing.feature1Sub": "Επιλέξτε ημερομηνίες & ώρες",
    "landing.feature2Title": "Πίνακας διαχειριστή",
    "landing.feature2Sub": "Γρήγορη επικοινωνία με διαιτητές",
    "landing.feature3Title": "Ροή τουρνουά",
    "landing.feature3Sub": "Άμεσες ενημερώσεις για όλους",
    "landing.contactDialogTitle": "Επικοινωνία",
    "landing.contactMessagePlaceholder": "Πείτε μας λίγα λόγια για εσάς και αυτό που αναζητάτε.",
    "landing.sendMessage": "Αποστολή μηνύματος",
    "landing.sending": "Αποστολή…",
    "landing.contactSuccess": "Το μήνυμα στάλθηκε. Θα επικοινωνήσουμε σύντομα.",

    // Login
    "login.title": "Σύνδεση στον λογαριασμό σας",
    "login.subtitle": "Εισάγετε τα στοιχεία σας παρακάτω.",
    "login.forgot": "Ξεχάσατε τον κωδικό;",
    "login.noAccount": "Δεν έχετε λογαριασμό;",
    "login.createOne": "Δημιουργήστε έναν",
    "login.welcomeBack": "Καλώς ήρθες, {name}",

    // Register
    "register.title": "Δημιουργία λογαριασμού",
    "register.subtitle": "Δημιουργήστε λογαριασμό για να διαχειρίζεστε τη διαθεσιμότητα και την αμοιβή σας",
    "register.confirmPassword": "Επιβεβαίωση κωδικού",
    "register.alreadyAccount": "Έχετε ήδη λογαριασμό;",
    "register.submitSuccess": "Ο λογαριασμός υποβλήθηκε. Αναμένει έγκριση διαχειριστή.",
    "register.awaitingApproval": "Αναμένει έγκριση",
    "register.submittedTitle": "Η εγγραφή υποβλήθηκε",
    "register.submittedBody": "Γεια σας {name}, ο διαχειριστής θα ελέγξει τον λογαριασμό σας ({email}) και θα τον ενεργοποιήσει σύντομα. Θα μπορείτε να συνδεθείτε μόλις η κατάσταση γίνει Επιβεβαιωμένος.",
    "register.goToLogin": "Μετάβαση στη σύνδεση",

    // Forgot password
    "forgot.backToLogin": "Επιστροφή στη σύνδεση",
    "forgot.title": "Ξεχάσατε τον κωδικό σας;",
    "forgot.subtitle": "Εισάγετε το email σας για να λάβετε σύνδεσμο επαναφοράς.",
    "forgot.send": "Αποστολή συνδέσμου",
    "forgot.sending": "Αποστολή…",
    "forgot.success": "Εάν υπάρχει αυτός ο λογαριασμός, θα λάβετε σύνδεσμο επαναφοράς.",
    "forgot.devLink": "Dev σύνδεσμος επαναφοράς",

    // Reset password
    "reset.label": "Επαναφορά κωδικού",
    "reset.title": "Ορίστε νέο κωδικό",
    "reset.subtitle": "Πρέπει να είναι τουλάχιστον 6 χαρακτήρες.",
    "reset.newPassword": "Νέος κωδικός",
    "reset.confirm": "Επιβεβαίωση κωδικού",
    "reset.submit": "Επαναφορά κωδικού",
    "reset.submitting": "Επαναφορά…",
    "reset.success": "Ο κωδικός επαναφέρθηκε. Παρακαλώ συνδεθείτε.",
    "reset.missingToken": "Λείπει το token επαναφοράς",
    "reset.backToLogin": "Επιστροφή στη σύνδεση",

    // User Dashboard
    "dashboard.heading": "Πίνακας ελέγχου",
    "dashboard.hello": "Γεια, {name}.",
    "dashboard.markAvailability": "Οι μπλε ημερομηνίες στο ημερολόγιο είναι προγραμματισμένες εκδηλώσεις. Κάντε κλικ για να δείτε λεπτομέρειες και να δηλώσετε τη διαθεσιμότητα και την τιμή σας.",
    "dashboard.eventCalendar": "Ημερολόγιο εκδηλώσεων",
    "dashboard.legendYourSlots": "Οι διαθεσιμότητές σας",
    "dashboard.legendTournaments": "Τουρνουά",
    "dashboard.selectedDate": "Επιλεγμένη ημερομηνία",
    "dashboard.workOptionLabel": "Επιλογές εργασίας",
    "dashboard.selectedTournamentLabel": "Επιλεγμένο Τουρνουά",
    "dashboard.selectedTournamentPlaceholder": "Όνομα τουρνουά",
    "dashboard.selectedTournamentRequired": "Παρακαλώ επιλέξτε ένα τουρνουά",
    "dashboard.workMorning": "Πρωί",
    "dashboard.workAfternoon": "Απόγευμα",
    "dashboard.workAllDay": "Όλη μέρα",
    "dashboard.commentPlaceholder": "π.χ. Από 10:00 π.μ. έως 1:00 μ.μ.",
    "dashboard.commentLabel": "Δηλώστε περισσότερες λεπτομέρειες για τη διαθεσιμότητά σας",
    "dashboard.suggestedPriceLabel": "Προτεινόμενη Αμοιβή",
    "dashboard.suggestedPricePlaceholder": "π.χ. €/Ώρα, €/Ημέρα",
    "dashboard.addAvailability": "Προσθήκη διαθεσιμότητας",
    "dashboard.saving": "Αποθήκευση…",
    "dashboard.availabilityAdded": "Η διαθεσιμότητα προστέθηκε",
    "dashboard.removed": "Αφαιρέθηκε",
    "dashboard.pickDateFirst": "Επιλέξτε ημερομηνία πρώτα",
    "dashboard.yourAvailability": "Διαθεσιμότητα & Τιμή",
    "dashboard.slotsCount": "{n} διαθεσιμότητες",
    "dashboard.noSlots": "Δεν έχετε δηλώσει διαθεσιμότητες ακόμη.",
    "dashboard.upcoming": "Επερχόμενα",
    "dashboard.tournaments": "Τουρνουά",
    "dashboard.activeCount": "{n} ενεργά",
    "dashboard.noTournaments": "Δεν υπάρχουν τουρνουά ακόμη. Ελέγξτε ξανά σύντομα.",
    "dashboard.tournamentsOn": "{date} · Τουρνουά",
    "dashboard.ageLabel": "Κατηγορία",
    "dashboard.ageGroup": "Ηλικιακή κατηγορία",
    "dashboard.format": "Μορφή",
    "dashboard.viewOnMap": "Προβολή στον χάρτη",
    "dashboard.websiteUrl": "Ιστοσελίδα",
    "dashboard.sportType": "Είδος αθλήματος",

    // Profile
    "profile.back": "Επιστροφή στον πίνακα",
    "profile.kicker": "Προφίλ",
    "profile.title": "Ο λογαριασμός σας",
    "profile.photoSection": "Φωτογραφία προφίλ",
    "profile.uploadPhoto": "Μεταφόρτωση φωτογραφίας",
    "profile.changePhoto": "Αλλαγή φωτογραφίας",
    "profile.uploading": "Μεταφόρτωση…",
    "profile.photoUpdated": "Η φωτογραφία ενημερώθηκε",
    "profile.personalSection": "Προσωπικά στοιχεία",
    "profile.personalSub": "Έτος γέννησης (μόνο το έτος) και πού ζείτε αυτή τη στιγμή.",
    "profile.birthYear": "Έτος γέννησης",
    "profile.birthYearPlaceholder": "π.χ. 1995",
    "profile.location": "Τόπος διαμονής",
    "profile.locationPlaceholder": "π.χ. Έγκωμη, Λευκωσία",
    "profile.saveDetails": "Αποθήκευση στοιχείων",
    "profile.detailsSaved": "Τα προσωπικά στοιχεία αποθηκεύτηκαν",
    "profile.yearRangeError": "Το έτος γέννησης πρέπει να είναι μεταξύ 1900 και {max}",
    "profile.sportsSection": "Αθλήματα & εμπειρία",
    "profile.sportsSub": "Προσθέστε κάθε άθλημα που διαιτητεύετε, με έτη εμπειρίας.",
    "profile.experienceLabel": "Εμπειρία διαιτητή",
    "profile.experienceYes": "Ναι",
    "profile.experienceNo": "Όχι",
    "profile.interestedSportsLabel": "Αθλήματα που σας ενδιαφέρουν",
    "profile.interestedSportsPlaceholder": "Σε ποια αθλήματα ενδιαφέρεστε να εκπαιδευτείτε ως διαιτητής;",
    "profile.addSport": "Προσθήκη αθλήματος",
    "profile.noSports": "Δεν έχετε προσθέσει αθλήματα. Κάντε κλικ στο «Προσθήκη αθλήματος».",
    "profile.sport": "Άθλημα",
    "profile.years": "Έτη",
    "profile.yearsPlaceholder": "π.χ. 1 έτος",
    "profile.saveSports": "Αποθήκευση αθλημάτων",
    "profile.sportsSaved": "Αθλήματα & εμπειρία αποθηκεύτηκαν",
    "profile.selectSportError": "Παρακαλώ επιλέξτε άθλημα για κάθε εγγραφή",
    "profile.selectSportPlaceholder": "Επιλέξτε άθλημα από τη λίστα",
    "profile.duplicateSport": "Διπλό άθλημα: {name}",
    "profile.paymentsSection": "Τρόποι πληρωμής",
    "profile.paymentsSub": "Προσθέστε τους τρόπους πληρωμής που δέχεστε και τα στοιχεία τους.",
    "profile.addPaymentMethod": "Προσθήκη τρόπου",
    "profile.paymentMethod": "Τρόπος πληρωμής",
    "profile.paymentDetails": "Στοιχεία",
    "profile.paymentDetailsPlaceholder": "π.χ. αριθμός λογαριασμού, τηλέφωνο, IBAN…",
    "profile.selectMethodPlaceholder": "Επιλέξτε τρόπο πληρωμής",
    "profile.noPayments": "Δεν έχουν προστεθεί τρόποι πληρωμής. Πατήστε «Προσθήκη τρόπου» για να ξεκινήσετε.",
    "profile.savePayments": "Αποθήκευση τρόπων πληρωμής",
    "profile.paymentsSaved": "Οι τρόποι πληρωμής αποθηκεύτηκαν",
    "profile.selectMethodError": "Παρακαλώ επιλέξτε τρόπο πληρωμής για κάθε γραμμή",
    "profile.duplicateMethod": "Διπλός τρόπος πληρωμής: {name}",

    // Admin
    "admin.kicker": "Διαχείριση / επισκόπηση",
    "admin.title": "Κέντρο ελέγχου",
    "admin.statReferees": "Διαιτητές",
    "admin.statSlots": "Διαθεσιμότητες",
    "admin.statTournaments": "Τουρνουά",
    "admin.tabUsers": "Χρήστες & Διαθεσιμότητα",
    "admin.tabTournaments": "Τουρνουά",
    "admin.tabRestore": "Κάδος Ανακύκλωσης",
    "admin.restoreHeading": "Διαγραμμένα τουρνουά",
    "admin.restoreEmpty": "Δεν υπάρχουν διαγραμμένα τουρνουά. Όταν διαγράψετε κάποιο από την καρτέλα Τουρνουά, θα εμφανιστεί εδώ.",
    "admin.deletedOn": "Διαγράφηκε στις",
    "admin.restoreTournament": "Επαναφορά τουρνουά",
    "admin.tournamentRestored": "Το τουρνουά επαναφέρθηκε",
    "admin.permanentlyDelete": "Οριστική διαγραφή",
    "admin.tournamentPermDeleted": "Το τουρνουά διαγράφηκε οριστικά",
    "admin.permDeleteConfirmTitle": "Οριστική διαγραφή αυτού του τουρνουά;",
    "admin.permDeleteConfirmBody": "Είστε σίγουροι ότι θέλετε να διαγράψετε οριστικά αυτό το τουρνουά;",
    "admin.permDeleteConfirmAction": "Ναι",
    "admin.permDeleteConfirmCancel": "Όχι",
    "admin.restoreConfirmTitle": "Επαναφορά αυτού του τουρνουά;",
    "admin.restoreConfirmBody": "Είστε σίγουροι ότι θέλετε να επαναφέρετε αυτό το τουρνουά;",
    "admin.restoreConfirmAction": "Ναι",
    "admin.restoreConfirmCancel": "Όχι",
    "admin.softDeleteConfirmTitle": "Μετακίνηση στον Κάδο Ανακύκλωσης;",
    "admin.softDeleteConfirmBody": "Είστε σίγουροι ότι θέλετε να μετακινήσετε στον Κάδο Ανακύκλωσης;",
    "admin.softDeleteConfirmYes": "Ναι",
    "admin.softDeleteConfirmNo": "Όχι",
    "admin.editProfileTitle": "Επεξεργασία προφίλ — {name}",
    "admin.updateProfile": "Ενημέρωση",
    "admin.editConfirmTitle": "Ενημέρωση στοιχείων προφίλ;",
    "admin.editConfirmBody": "Θέλετε να ενημερώσετε τα στοιχεία του προφίλ;",
    "admin.profileUpdated": "Το προφίλ ενημερώθηκε.",
    "admin.noEntries": "Δεν υπάρχουν καταχωρήσεις ακόμα. Κάντε κλικ στο Προσθήκη για να εισαγάγετε.",
    "admin.sportPlaceholder": "Επιλέξτε άθλημα",
    "admin.yearsPlaceholder": "Έτη",
    "admin.paymentMethodPlaceholder": "Επιλέξτε μέθοδο",
    "admin.paymentDetailsPlaceholder": "Λογαριασμός / στοιχεία",
    "admin.allTournaments": "Όλα τα τουρνουά",
    "admin.addTournament": "Προσθήκη τουρνουά",
    "admin.noTournamentsAdmin": "Δεν υπάρχουν τουρνουά ακόμη. Προσθέστε ένα για να δημοσιευτεί.",
    "admin.registeredReferees": "Εγγεγραμμένοι διαιτητές",
    "admin.searchPlaceholder": "Αναζήτηση: όνομα, επώνυμο, κατάσταση, email, έτος γέννησης, κινητό, τόπος, άθλημα, πληρωμή…",
    "admin.deleteSelected": "Διαγραφή επιλεγμένων ({n})",
    "admin.colUsername": "Όνομα",
    "admin.colStatus": "Κατάσταση",
    "admin.colNotes": "Σημειώσεις",
    "admin.colEmail": "Email",
    "admin.colBirthYear": "Έτος γέννησης",
    "admin.colMobile": "Κινητό",
    "admin.colLocation": "Τόπος διαμονής",
    "admin.colSportExperience": "Άθλημα & Εμπειρία",
    "admin.colPaymentMethod": "Τρόπος πληρωμής",
    "admin.colSlots": "Διαθεσιμότητες",
    "admin.noUsers": "Δεν υπάρχουν χρήστες ακόμη.",
    "admin.noUsersMatch": "Κανένας χρήστης δεν ταιριάζει στην αναζήτηση.",
    "admin.verified": "Επιβεβαιωμένος",
    "admin.pending": "Σε εκκρεμότητα",
    "admin.activate": "Ενεργοποίηση",
    "admin.deactivate": "Απενεργοποίηση",
    "admin.activated": "Ο λογαριασμός ενεργοποιήθηκε (Επιβεβαιωμένος)",
    "admin.deactivated": "Ο λογαριασμός απενεργοποιήθηκε (Σε εκκρεμότητα)",
    "admin.refereesAvailability": "Διαθεσιμότητα διαιτητών",
    "admin.filterFrom": "Από",
    "admin.filterTo": "Έως",
    "admin.exportLabel": "Εξαγωγή ({n})",
    "admin.colDate": "Ημερομηνία",
    "admin.colOption": "Επιλογή",
    "admin.noAvailability": "Καμία διαθεσιμότητα δεν ταιριάζει με το φίλτρο.",
    "admin.exportSuccess": "Εξάχθηκαν {n} εγγραφές ως {kind}",
    "admin.exportEmpty": "Δεν υπάρχουν εγγραφές για εξαγωγή",
    "admin.exportFailed": "Η εξαγωγή απέτυχε",
    "admin.notesForUser": "Σημειώσεις για {name}",
    "admin.userNotesPlaceholder": "Γράψτε σημειώσεις για αυτόν τον διαιτητή — οτιδήποτε βοηθά να θυμάστε (δεξιότητες, γλώσσες, προτιμήσεις επικοινωνίας, κλπ.)",
    "admin.saveNotes": "Αποθήκευση σημειώσεων",
    "admin.notesSaved": "Οι σημειώσεις αποθηκεύτηκαν",
    "admin.availNotesPlaceholder": "Γράψτε σημειώσεις για αυτή τη συγκεκριμένη διαθεσιμότητα (ανάθεση τουρνουά, follow-up, κλπ.)",
    "admin.confirmDeleteTitle": "Επιβεβαίωση οριστικής διαγραφής του χρήστη από το σύστημα.",
    "admin.confirmDeleteBody": "Αυτό θα αφαιρέσει {n} διαιτητή/ές μαζί με τις εγγραφές διαθεσιμότητας, tokens επιβεβαίωσης, tokens επαναφοράς κωδικού και τις αναφορές avatar. Η ενέργεια αυτή δεν αναιρείται.",
    "admin.confirmDeleteAction": "Ναι, οριστική διαγραφή",
    "admin.confirmDeleteAvailTitle": "Διαγραφή {n} εγγραφής/ών διαθεσιμότητας;",
    "admin.confirmDeleteAvailBody": "Αυτό θα αφαιρέσει οριστικά {n} εγγραφή/ές διαθεσιμότητας. Η ενέργεια δεν αναιρείται.",
    "admin.availabilityDeleted": "{n} εγγραφή/ές διαθεσιμότητας διαγράφηκαν.",
    "admin.availSearchPlaceholder": "Αναζήτηση: επιλογή, όνομα, επώνυμο, email, κινητό, διαθεσιμότητα, αμοιβή…",
    "admin.deletedCount": "Διαγράφηκαν {n} χρήστης/ες οριστικά",
    "admin.deleteFailedCount": "{n} διαγραφή/ές απέτυχαν",
    "admin.tournamentRemoved": "Το τουρνουά αφαιρέθηκε",
    "admin.tournamentCreated": "Το τουρνουά δημιουργήθηκε",
    "admin.tournamentUpdated": "Το τουρνουά ενημερώθηκε",
    "admin.pickStartEnd": "Επιλέξτε ημερομηνία έναρξης και λήξης",
    "admin.endBeforeStart": "Η ημερομηνία λήξης πρέπει να είναι ίδια ή μετά την έναρξη",

    // Tournament form
    "tdialog.title": "Προσθήκη τουρνουά",
    "tdialog.editTitle": "Επεξεργασία τουρνουά",
    "tdialog.cloneTitle": "Αντιγραφή τουρνουά",
    "common.clone": "Αντιγραφή",
    "tdialog.eventName": "Όνομα τουρνουά",
    "tdialog.tournamentUrl": "URL τουρνουά",
    "common.eventWebsite": "Ιστότοπος εκδήλωσης",
    "tdialog.sportType": "Είδος αθλήματος",
    "tdialog.eventLocation": "Τοποθεσία χάρτη",
    "tdialog.ageCategories": "Ηλικιακές κατηγορίες",
    "tdialog.formatCategories": "Κατηγορίες μορφής",
    "tdialog.otherDetails": "Άλλες λεπτομέρειες",
    "tdialog.otherDetailsPlaceholder": "Οποιαδήποτε πρόσθετη πληροφορία για την εκδήλωση…",
    "tdialog.startDate": "Ημερομηνία έναρξης",
    "tdialog.endDate": "Ημερομηνία λήξης",
    "tdialog.pickStart": "Επιλέξτε ημερομηνία έναρξης",
    "tdialog.pickEnd": "Επιλέξτε ημερομηνία λήξης",
    "tdialog.logo": "Λογότυπο (προαιρετικό)",

    // Language switcher
    "lang.label": "Γλώσσα",
};

export const DICTIONARIES = { en, el };

export function translate(lang, key, vars) {
    const dict = DICTIONARIES[lang] || DICTIONARIES.en;
    let str = dict[key];
    if (str === undefined) str = DICTIONARIES.en[key];
    if (str === undefined) return key;
    if (vars) {
        for (const k of Object.keys(vars)) {
            str = str.replace(new RegExp(`{${k}}`, "g"), String(vars[k]));
        }
    }
    return str;
}
